import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  CheckCircle2, 
  XCircle,
  Clock,
  User,
  MessageSquare
} from 'lucide-react';
import { checklistApprovalsApi, rejectionReasonsApi, RejectionReason } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { useState } from 'react';

export function ChecklistApprovalsPage() {
  const { hapticFeedback, showAlert } = useTelegram();
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [selectedRejectionReasonId, setSelectedRejectionReasonId] = useState<string>('');

  const { data: instances, isLoading } = useQuery({
    queryKey: ['checklist-approvals-pending'],
    queryFn: () => checklistApprovalsApi.getPending(),
  });

  // Получаем причины отклонения для первого проекта (если есть)
  const projectId = instances?.[0]?.assignment?.template?.projectId;
  const { data: rejectionReasons } = useQuery({
    queryKey: ['rejection-reasons', projectId],
    queryFn: () => rejectionReasonsApi.list({ projectId: projectId || undefined }),
    enabled: !!projectId,
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => checklistApprovalsApi.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-approvals-pending'] });
      hapticFeedback('success');
      showAlert('Задача принята');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось принять задачу');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, comment, rejectionReasonId }: { id: string; comment?: string; rejectionReasonId?: string }) =>
      checklistApprovalsApi.reject(id, { comment, rejectionReasonId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-approvals-pending'] });
      setRejectingId(null);
      setRejectComment('');
      setSelectedRejectionReasonId('');
      hapticFeedback('success');
      showAlert('Задача отклонена');
    },
    onError: (error: any) => {
      hapticFeedback('error');
      showAlert(error.message || 'Не удалось отклонить задачу');
    },
  });

  const handleApprove = (id: string) => {
    hapticFeedback('medium');
    approveMutation.mutate(id);
  };

  const handleReject = (id: string) => {
    if (!rejectComment.trim() && !selectedRejectionReasonId) {
      showAlert('Укажите причину отклонения или выберите из справочника');
      return;
    }
    hapticFeedback('medium');
    rejectMutation.mutate({ 
      id, 
      comment: rejectComment.trim() || undefined,
      rejectionReasonId: selectedRejectionReasonId || undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div>
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <CheckCircle2 className="w-6 h-6" />
          На подтверждение
        </h1>
        <p className="text-sm text-tg-hint mt-1">
          {instances?.length || 0} задач ожидают подтверждения
        </p>
      </div>

      {/* Список задач */}
      <div className="space-y-3">
        {!instances || instances.length === 0 ? (
          <Card className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-tg-hint" />
            <p className="text-tg-hint">Нет задач на подтверждение</p>
          </Card>
        ) : (
          instances.map((instance) => (
            <Card key={instance.id} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-tg-text">{instance.rule?.text}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="warning">Ожидает приёмки</Badge>
                    {instance.rule?.recurrence && (
                      <Badge variant="default">
                        {instance.rule.recurrence === 'DAILY' && 'Ежедневно'}
                        {instance.rule.recurrence === 'WEEKLY' && 'Еженедельно'}
                        {instance.rule.recurrence === 'MONTHLY' && 'Ежемесячно'}
                        {instance.rule.recurrence === 'ONCE' && 'Один раз'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2 mt-3 text-sm text-tg-hint">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>
                    {instance.assignment?.assignee?.firstName}{' '}
                    {instance.assignment?.assignee?.lastName}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Дедлайн: {format(new Date(instance.dueAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                  </span>
                </div>
                {instance.completedAt && (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>
                      Выполнено: {format(new Date(instance.completedAt), 'dd.MM.yyyy HH:mm', { locale: ru })}
                    </span>
                  </div>
                )}
                {instance.comments && instance.comments.length > 0 && (
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 mt-0.5" />
                    <div className="flex-1 space-y-1">
                      {[...instance.comments]
                        .sort(
                          (a, b) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime()
                        )
                        .map((comment) => (
                          <div key={comment.id} className="text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium">
                                {comment.user?.firstName} {comment.user?.lastName}
                              </span>
                              <span className="text-[10px] text-tg-hint">
                                {format(new Date(comment.createdAt), 'dd.MM.yyyy HH:mm', {
                                  locale: ru,
                                })}
                              </span>
                            </div>
                            <div className="text-tg-hint whitespace-pre-wrap break-words">
                              {comment.text}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {rejectingId === instance.id ? (
                <div className="mt-4 space-y-3">
                  {/* Выбор причины из справочника */}
                  {rejectionReasons && rejectionReasons.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-tg-text mb-2 block">
                        Причина отклонения (из справочника)
                      </label>
                      <select
                        value={selectedRejectionReasonId}
                        onChange={(e) => {
                          setSelectedRejectionReasonId(e.target.value);
                          if (e.target.value) {
                            setRejectComment(''); // Очищаем комментарий при выборе причины
                          }
                        }}
                        className="w-full p-2 border rounded-lg text-sm"
                      >
                        <option value="">-- Выберите причину --</option>
                        {rejectionReasons
                          .filter((r: RejectionReason) => r.isActive)
                          .map((reason: RejectionReason) => (
                            <option key={reason.id} value={reason.id}>
                              {reason.text}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Комментарий (опционально, если выбрана причина) */}
                  <div>
                    <label className="text-sm font-medium text-tg-text mb-2 block">
                      Комментарий {!selectedRejectionReasonId && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={rejectComment}
                      onChange={(e) => setRejectComment(e.target.value)}
                      placeholder={selectedRejectionReasonId ? "Дополнительный комментарий (необязательно)..." : "Укажите причину отклонения..."}
                      className="w-full p-2 border rounded-lg text-sm"
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setRejectingId(null);
                        setRejectComment('');
                        setSelectedRejectionReasonId('');
                      }}
                    >
                      Отмена
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleReject(instance.id)}
                      disabled={(!rejectComment.trim() && !selectedRejectionReasonId) || rejectMutation.isPending}
                    >
                      Отклонить
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleApprove(instance.id)}
                    disabled={approveMutation.isPending}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Принять
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      setRejectingId(instance.id);
                      hapticFeedback('light');
                    }}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Отклонить
                  </Button>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

